import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AnnotationView — presentational, mirrors `BlockView`.
 *
 * `BlockView` receives a resolved `ContentComponent` (chosen by
 * `resolveContentComponent(block)` from `contentRegistry`) and renders
 * it. `AnnotationView` does the same: it receives a resolved registry
 * entry and renders the type-specific Component.
 *
 * The container (`AnnotationViewContainer`, this file's sibling) owns
 * data — it subscribes to the userAnnotationStore and resolves a
 * registry entry per row. Per-type views (under `views/`) own paint —
 * they render the actual sticker / drawing / highlight / pin.
 *
 * What's left in this file is the bridge: for read-only types (drawing,
 * text_highlight, comment) the bridge is trivial — pass the props
 * straight through. For mark types (sticker, stamp) the bridge wraps
 * the inner view in a `useMarkGestures` hook that owns the drag /
 * resize / rotate / spin state machine and bakes the resulting
 * `transform` into the `style` prop the inner view consumes.
 *
 * Anyone reading this alongside `block-view.tsx` should see the same
 * shape: `<X Component={...} />` driven by a registry, with the
 * Container subscribing to a store one level up.
 */
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { UnsupportedAnnotationView, } from "./annotation-registry";
import { AnnotationSelectionChrome } from "./annotation-selection-chrome";
import { annotationTransformStyle } from "./annotation-transform";
export const AnnotationView = memo(function AnnotationView({ annotation, entry, position, mode, containerRef, handlers, drawingSelection, markSelection, }) {
    if (!entry) {
        return (_jsx(UnsupportedAnnotationView, { annotation: annotation, mode: mode, containerRef: containerRef }));
    }
    if (entry.kind === "mark") {
        return (_jsx(MarkInteractiveView, { annotation: annotation, Component: entry.Component, position: position, mode: mode, containerRef: containerRef, handlers: handlers, selection: markSelection }));
    }
    // Read-only path: drawing / text_highlight / comment paint
    // themselves from their own content / anchor. Drawings opt back
    // into interactivity in expanded mode via `drawingSelection`;
    // other read-only types receive it as `undefined` and ignore.
    // (text_highlight + text-range comment rows live in
    // `InlineAnnotationsLayer`, not here — see that file's comment
    // for why.)
    const Component = entry.Component;
    return (_jsx(Component, { annotation: annotation, mode: mode, containerRef: containerRef, drawingSelection: drawingSelection }));
});
// ---------------------------------------------------------------------------
// Mark interactive view — gesture stack + inner-view bridge.
//
// `sticker`, `stamp`, and emoji-reaction annotations share the same
// gesture model. Today this view owns:
//
//   - Body drag (move) gesture — selecting + dragging in one
//     pointer-down → move sequence.
//   - Double-click flick — a decaying spin + scale jolt that reads
//     as "playful". Persisted at flick-end via the same onPatch
//     callback.
//
// Resize + rotate live in the SHARED `AnnotationSelectionChrome` so
// drawings and marks use ONE gesture stack for those two axes.
// `MarkInteractiveView` mounts the chrome as a child of its wrapper
// when the mark is interactive — the chrome's `inset: 0` outline
// hugs the wrapper's rendered bbox and the corner handles orbit it.
//
// The inner view is the registry-resolved `Component` — the gesture
// stack passes it the same `MarkComponentProps` shape regardless of
// type, so adding a third mark type means flipping its registry
// `kind` and that's it.
// ---------------------------------------------------------------------------
// Drag clamp: allow the annotation's center to overshoot the host by
// 10% of the host's dimension on any edge.
const POS_CLAMP_MIN = -0.1;
const POS_CLAMP_MAX = 1.1;
function clampPosition(v) {
    if (v < POS_CLAMP_MIN)
        return POS_CLAMP_MIN;
    if (v > POS_CLAMP_MAX)
        return POS_CLAMP_MAX;
    return v;
}
// Scale clamp for the double-click flick. The drag gesture in
// `AnnotationSelectionChrome` already enforces a defense-in-depth
// floor (`MIN_SCALE = 0.05`); these tighter bounds keep the
// double-click jolt within a sane "playful" range.
const SCALE_MIN = 0.3;
const SCALE_MAX = 3;
function clampScale(s) {
    if (s < SCALE_MIN)
        return SCALE_MIN;
    if (s > SCALE_MAX)
        return SCALE_MAX;
    return s;
}
// Double-click flick: a guaranteed-visible angular impulse, integrated
// frame-by-frame and decayed by exponential friction until the sticker
// settles at a new resting rotation.
//
// Velocity range: [720°/s, 1440°/s] = 2–4 turns/sec. The minimum rules
// out near-zero rolls so every double-click reads as a real spin.
//
// Friction: `velocity *= e^(-FRICTION * dt)` per frame. With
// FRICTION = 2.0, velocity halves about every 0.35s and a top-end flick
// settles in ~3s — long enough to feel like physics, short enough to
// stay playful.
//
// Stop threshold: once |velocity| dips below 5°/s the spin commits its
// current rotation and the rAF loop exits. The hard duration cap is a
// safety net in case the math ever goes sideways.
const DBLCLICK_SPIN_MIN_DEG_PER_SEC = 720;
const DBLCLICK_SPIN_MAX_DEG_PER_SEC = 1440;
const SPIN_FRICTION_PER_SEC = 2.0;
const SPIN_STOP_THRESHOLD_DEG_PER_SEC = 5;
const SPIN_MAX_DURATION_MS = 8000;
// Double-click scale jolt: alongside the angular flick, every double-tap
// also rescales the sticker by ±[10%, 30%] of its current size.
// Direction is normally random, but forced toward the safe side when
// the sticker is bumping the SCALE_MIN/SCALE_MAX clamp so the change
// always lands somewhere meaningfully different (no silent no-op at the
// boundary).
const DBLCLICK_SCALE_MIN_DELTA = 0.1;
const DBLCLICK_SCALE_MAX_DELTA = 0.3;
const SCALE_FORCE_GROW_THRESHOLD = SCALE_MIN / (1 - DBLCLICK_SCALE_MIN_DELTA);
const SCALE_FORCE_SHRINK_THRESHOLD = SCALE_MAX / (1 + DBLCLICK_SCALE_MIN_DELTA);
// Scale ease rate (per second) used by the spin's rAF loop to tween the
// rendered scale toward the double-click target. With k=8, scale crosses
// ~63% of the remaining distance every 0.125s and is essentially settled
// in ~0.5s — visibly easing while the spin is still going (which decays
// over ~3s), not snapping at the start.
const SPIN_SCALE_EASE_PER_SEC = 8;
// Once the gap to target is below this fraction of the target, snap and
// stop tweening — exponential ease never literally reaches the target.
const SPIN_SCALE_EPSILON = 0.001;
function pickScaleSign(currentScale) {
    if (currentScale <= SCALE_FORCE_GROW_THRESHOLD)
        return 1;
    if (currentScale >= SCALE_FORCE_SHRINK_THRESHOLD)
        return -1;
    return Math.random() < 0.5 ? -1 : 1;
}
function signedRandom(min, max, sign) {
    const magnitude = min + Math.random() * (max - min);
    const direction = sign ?? (Math.random() < 0.5 ? -1 : 1);
    return magnitude * direction;
}
const MarkInteractiveView = memo(function MarkInteractiveView({ annotation, Component, position, mode, containerRef, handlers, selection, }) {
    const interactive = mode === "expanded" && handlers !== undefined;
    // Selected when the caller wires selection AND this annotation's
    // id matches the selected one. Without `selection`, the mark is
    // never selected — chrome never paints — and the mark behaves as
    // a body-drag-only affordance.
    const isSelected = selection?.isSelected === true;
    // Body-drag gesture only — resize / rotate live in the shared
    // `AnnotationSelectionChrome` rendered as a child below when
    // `isSelected`.
    const [isDragging, setIsDragging] = useState(false);
    // -- Spin physics ---------------------------------------------------------
    // Double-click flicks the sticker into a decaying spin *and* tweens its
    // scale toward a fresh target. While the loop runs, `spinRotationDeg`
    // overrides the rendered rotation and `spinScale` overrides the
    // rendered scale; the persisted rotation/scale only update once at
    // the end, when the rotation settles below the stop threshold.
    const [spinRotationDeg, setSpinRotationDeg] = useState(null);
    const [spinScale, setSpinScale] = useState(null);
    const spinRotationRef = useRef(0);
    const spinVelocityRef = useRef(0);
    const spinScaleRef = useRef(1);
    const spinScaleTargetRef = useRef(1);
    const spinStartTimestampRef = useRef(0);
    const spinLastTimestampRef = useRef(0);
    const spinAnimRef = useRef(null);
    const cancelSpinAnim = useCallback(() => {
        if (spinAnimRef.current !== null) {
            cancelAnimationFrame(spinAnimRef.current);
            spinAnimRef.current = null;
        }
    }, []);
    // Cancel any in-flight rAF when the component unmounts so we don't
    // call setState on a torn-down component.
    useEffect(() => cancelSpinAnim, [cancelSpinAnim]);
    /**
     * Stop any in-flight spin and commit its current rotation + scale to
     * the parent. Returns the rotation that should be considered "live"
     * right now — either the spin's last frame, or the persisted rotation
     * if no spin was running. Callers that need the freshest rotation
     * (e.g. the rotate gesture seeding `initialRotation`) should use this
     * rather than the closure-captured `position.rotation`, which can
     * lag a frame behind the spin's commit.
     */
    const stopSpinAndCommit = useCallback(() => {
        if (spinAnimRef.current === null)
            return position.rotation;
        cancelAnimationFrame(spinAnimRef.current);
        spinAnimRef.current = null;
        const finalRotation = spinRotationRef.current;
        const liveScale = spinScaleRef.current;
        const targetScale = spinScaleTargetRef.current;
        const settled = Math.abs(targetScale - liveScale) <= Math.abs(targetScale) * SPIN_SCALE_EPSILON;
        const finalScale = settled ? targetScale : liveScale;
        handlers?.onPatch(annotation.id, {
            rotation: finalRotation,
            scale: finalScale,
        });
        setSpinRotationDeg(null);
        setSpinScale(null);
        return finalRotation;
    }, [annotation.id, handlers, position.rotation]);
    // Pointer offset within the sticker at drag-start, in *normalized* coords.
    const grabOffsetRef = useRef({ x: 0, y: 0 });
    // -- Move gesture (sticker body) ------------------------------------------
    const onBodyPointerDown = useCallback((e) => {
        if (!interactive || !handlers)
            return;
        if (e.button !== 0 && e.pointerType === "mouse")
            return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0)
            return;
        stopSpinAndCommit();
        // Click selects (mirrors Figma's "tap to select"). Doing this
        // on pointer-down — not pointer-up — means a click-and-drag
        // both selects AND begins the move gesture in one motion. If
        // the click never moves we still leave the mark selected.
        // Skipped when the caller didn't wire a selection model
        // (legacy always-interactive callers); the mark just drags.
        if (selection && !selection.isSelected) {
            selection.onSelect(annotation.id);
        }
        const pointerNormX = (e.clientX - rect.left) / rect.width;
        const pointerNormY = (e.clientY - rect.top) / rect.height;
        grabOffsetRef.current = {
            x: pointerNormX - position.x,
            y: pointerNormY - position.y,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
        setIsDragging(true);
        e.stopPropagation();
    }, [
        interactive,
        handlers,
        position.x,
        position.y,
        containerRef,
        stopSpinAndCommit,
        annotation.id,
        selection,
    ]);
    const onBodyPointerMove = useCallback((e) => {
        if (!isDragging || !handlers)
            return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0)
            return;
        const pointerNormX = (e.clientX - rect.left) / rect.width;
        const pointerNormY = (e.clientY - rect.top) / rect.height;
        const nextX = clampPosition(pointerNormX - grabOffsetRef.current.x);
        const nextY = clampPosition(pointerNormY - grabOffsetRef.current.y);
        handlers.onPatch(annotation.id, { x: nextX, y: nextY });
    }, [isDragging, annotation.id, handlers, containerRef]);
    const onBodyPointerUp = useCallback((e) => {
        if (!isDragging)
            return;
        if (e.currentTarget.hasPointerCapture(e.pointerId)) {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }
        setIsDragging(false);
        // Notify the parent that the gesture is done. Per-axis callbacks
        // fire many times per frame; `onSettle` is the single commit
        // point for any debounced state (e.g. persisting the canonical
        // row to the server).
        handlers?.onSettle?.(annotation.id);
    }, [isDragging, annotation.id, handlers]);
    // -- Resize / rotate via shared chrome ------------------------------------
    //
    // The chrome itself owns the per-frame gesture math. We only map
    // its abstract `(scale, rotation)` patches back to the mark's
    // `AnnotationPosition` so they reach the same `handlers.onPatch`
    // callback the body drag uses.
    const onChromeScale = useCallback((next) => {
        handlers?.onPatch(annotation.id, { scale: next });
    }, [annotation.id, handlers]);
    const onChromeRotation = useCallback((next) => {
        handlers?.onPatch(annotation.id, { rotation: next });
    }, [annotation.id, handlers]);
    const onChromeSettle = useCallback(() => {
        handlers?.onSettle?.(annotation.id);
    }, [annotation.id, handlers]);
    const onDoubleClick = useCallback((e) => {
        if (!interactive || !handlers)
            return;
        e.stopPropagation();
        const baseScale = spinAnimRef.current !== null ? spinScaleRef.current : position.scale;
        const scaleSign = pickScaleSign(baseScale);
        const scaleDelta = signedRandom(DBLCLICK_SCALE_MIN_DELTA, DBLCLICK_SCALE_MAX_DELTA, scaleSign);
        const nextScaleTarget = clampScale(baseScale * (1 + scaleDelta));
        const flick = signedRandom(DBLCLICK_SPIN_MIN_DEG_PER_SEC, DBLCLICK_SPIN_MAX_DEG_PER_SEC);
        // Already spinning: add velocity to the current spin and reset
        // the scale target so the running rAF loop tweens toward the new
        // value from wherever the eased scale currently sits.
        if (spinAnimRef.current !== null) {
            spinVelocityRef.current += flick;
            spinScaleTargetRef.current = nextScaleTarget;
            return;
        }
        // Fresh spin: seed all the refs from the persisted rotation/scale.
        spinRotationRef.current = position.rotation;
        spinVelocityRef.current = flick;
        spinScaleRef.current = position.scale;
        spinScaleTargetRef.current = nextScaleTarget;
        spinStartTimestampRef.current = 0;
        spinLastTimestampRef.current = 0;
        setSpinRotationDeg(position.rotation);
        setSpinScale(position.scale);
        const step = (now) => {
            if (spinStartTimestampRef.current === 0) {
                spinStartTimestampRef.current = now;
                spinLastTimestampRef.current = now;
                spinAnimRef.current = requestAnimationFrame(step);
                return;
            }
            const dt = (now - spinLastTimestampRef.current) / 1000;
            spinLastTimestampRef.current = now;
            const elapsed = now - spinStartTimestampRef.current;
            spinVelocityRef.current *= Math.exp(-SPIN_FRICTION_PER_SEC * dt);
            spinRotationRef.current += spinVelocityRef.current * dt;
            setSpinRotationDeg(spinRotationRef.current);
            const target = spinScaleTargetRef.current;
            const remaining = target - spinScaleRef.current;
            if (Math.abs(remaining) > Math.abs(target) * SPIN_SCALE_EPSILON) {
                spinScaleRef.current += remaining * (1 - Math.exp(-SPIN_SCALE_EASE_PER_SEC * dt));
            }
            else {
                spinScaleRef.current = target;
            }
            setSpinScale(spinScaleRef.current);
            const settled = Math.abs(spinVelocityRef.current) < SPIN_STOP_THRESHOLD_DEG_PER_SEC;
            const timedOut = elapsed >= SPIN_MAX_DURATION_MS;
            if (settled || timedOut) {
                stopSpinAndCommit();
                return;
            }
            spinAnimRef.current = requestAnimationFrame(step);
        };
        spinAnimRef.current = requestAnimationFrame(step);
    }, [interactive, handlers, position.rotation, position.scale, stopSpinAndCommit]);
    const isSpinning = spinRotationDeg !== null;
    const renderedRotation = spinRotationDeg ?? position.rotation;
    const renderedScale = spinScale ?? position.scale;
    // Wrapper transform delegated to `annotationTransformStyle` — same
    // helper `DrawingAnnotationView` uses, so both annotation kinds emit
    // the same `translate(...) rotate(...) scale(...)` shape and the
    // same `--annotation-inverse-scale-x/y` CSS vars. Marks pass the
    // gesture's uniform `renderedScale` for both axes. Stream-vs-modal
    // sizing isn't carried here — `.annotations-container` owns it via
    // its own `transform: scale(--annotation-natural-scale)`. The
    // chrome's counter-scaling rule in `annotation.scss` divides
    // `inverse-scale` by `--annotation-natural-scale` so handles +
    // outlines stay at constant screen-px regardless of either factor.
    const positionStyle = annotationTransformStyle({
        centerX: position.x,
        centerY: position.y,
        rotation: renderedRotation,
        scaleX: renderedScale,
        scaleY: renderedScale,
        sizing: "intrinsic",
    });
    // Outer wrapper visual state — only the body-drag gesture lives
    // here now; resize / rotate are handled by the chrome below.
    const className = [
        "annotation-view",
        `annotation-view--${annotation.type}`,
        `annotation-view--mode-${mode}`,
        isDragging && "annotation-view--dragging",
        isSelected && "annotation-view--selected",
        isSpinning && "annotation-view--spinning",
    ]
        .filter(Boolean)
        .join(" ");
    return (_jsxs("div", { className: className, style: positionStyle, "data-annotation-id": annotation.id, onPointerDown: interactive ? onBodyPointerDown : undefined, onPointerMove: interactive ? onBodyPointerMove : undefined, onPointerUp: interactive ? onBodyPointerUp : undefined, onPointerCancel: interactive ? onBodyPointerUp : undefined, onDoubleClick: interactive ? onDoubleClick : undefined, children: [_jsx(Component, { annotation: annotation, mode: mode }), interactive && isSelected ? (_jsx(AnnotationSelectionChrome, { annotationId: annotation.id, containerRef: containerRef, centerX: position.x, centerY: position.y, scale: renderedScale, rotation: renderedRotation, onScale: onChromeScale, onRotation: onChromeRotation, onSettle: onChromeSettle })) : null] }));
});
